import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { EngineerHead } from './Engineer';
import { NormIllustration } from './NormIllustrations';
import type { NormEntry } from './catalog';

interface Props {
  norm: NormEntry | null;
  onClose: () => void;
}

export function NormDetailModal({ norm, onClose }: Props) {
  return (
    <Dialog.Root open={!!norm} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50"
          style={{ background: 'rgba(2,6,23,0.75)', backdropFilter: 'blur(8px)' }}
        />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[var(--radius-lg)] border shadow-2xl"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          {norm && (
            <div className="flex max-h-[85vh] flex-col">
              {/* Header */}
              <div className="relative h-40 shrink-0 overflow-hidden border-b" style={{ background: 'var(--surface-canvas)', borderColor: 'var(--border-subtle)' }}>
                <NormIllustration kind={norm.illustration} />
                <Dialog.Close asChild>
                  <button
                    aria-label="Cerrar"
                    className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] border"
                    style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}
                  >
                    <X size={14} strokeWidth={2} />
                  </button>
                </Dialog.Close>
                <div
                  className="absolute left-4 top-3 rounded-[var(--radius-sm)] border px-2 py-1 font-mono tabular text-[10px] tracking-wider"
                  style={{
                    background: 'var(--surface-1)',
                    borderColor: 'var(--border-default)',
                    color: 'var(--accent)',
                  }}
                >
                  {norm.code}
                </div>
              </div>

              {/* Body (scrollable) */}
              <div className="scrollbar-thin flex-1 overflow-y-auto px-6 py-5">
                <Dialog.Title asChild>
                  <h2
                    className="mb-1 text-xl"
                    style={{ fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '-0.02em' }}
                  >
                    {norm.title}
                  </h2>
                </Dialog.Title>
                <Dialog.Description asChild>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {norm.scope}
                  </p>
                </Dialog.Description>

                <p className="mt-4 text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  {norm.description}
                </p>

                {/* Engineer block */}
                <div
                  className="mt-5 flex items-start gap-3 rounded-[var(--radius-md)] border p-4"
                  style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}
                >
                  <div className="shrink-0">
                    <EngineerHead size={48} />
                  </div>
                  <div>
                    <div
                      className="text-[10px] uppercase tracking-[0.18em]"
                      style={{ color: 'var(--accent)' }}
                    >
                      El ingeniero dice
                    </div>
                    <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                      {norm.engineerSays}
                    </p>
                  </div>
                </div>

                {/* All highlights */}
                {norm.highlights.length > 0 && (
                  <section className="mt-6">
                    <h3 className="mb-3 text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                      Datos clave
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {norm.highlights.map((h) => (
                        <div
                          key={h.label}
                          className="rounded-[var(--radius-sm)] border p-3"
                          style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}
                        >
                          <div className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                            {h.label}
                          </div>
                          <div className="mt-1 font-mono tabular text-sm" style={{ color: 'var(--text-primary)' }}>
                            {h.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Applied in - full list with descriptions */}
                {norm.appliedIn.length > 0 && (
                  <section className="mt-6">
                    <h3 className="mb-3 text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                      Aplicada en AlphaConcreto
                    </h3>
                    <ul className="space-y-1.5">
                      {norm.appliedIn.map((a) => (
                        <li
                          key={a.module}
                          className="flex items-start gap-3 rounded-[var(--radius-sm)] border p-3"
                          style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}
                        >
                          <span
                            className="mt-0.5 inline-block h-2 w-2 shrink-0 rounded-full"
                            style={{ background: 'var(--accent)' }}
                          />
                          <div>
                            <div className="text-[12px]" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                              {a.module}
                            </div>
                            <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                              {a.what}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
